import type { Permission, RoleUsage } from '@prism/contracts';

/** Permissions a custom role may be given, grouped for the role editor. `admin` is never listed: only the Administrator role holds it. */
const PERMISSION_GROUPS: { title: string; permissions: Permission[] }[] = [
  { title: 'Library', permissions: ['asset.read', 'asset.download', 'asset.share', 'asset.upload', 'asset.edit_metadata', 'asset.delete', 'asset.delete_versions', 'asset.classify', 'collection.public', 'group.manage', 'rights.manage'] },
  { title: 'Production', permissions: ['sequence.edit', 'sequence.take_over', 'export.create', 'processing.retry'] },
  { title: 'Automation', permissions: ['automation.manage', 'automation.run'] },
  { title: 'Administration', permissions: ['storage.manage', 'access.manage'] },
  { title: 'Media portal', permissions: ['portal.review', 'portal.assign', 'portal.manage'] },
];

/** The groups with every permission placed once; anything not listed above (except admin) ends up under "Other". */
export function groupPermissions(all: readonly Permission[]): { title: string; permissions: Permission[] }[] {
  const listed = new Set(PERMISSION_GROUPS.flatMap((g) => g.permissions));
  const other = all.filter((p) => p !== 'admin' && !listed.has(p));
  const groups = PERMISSION_GROUPS.map((g) => ({ title: g.title, permissions: g.permissions.filter((p) => all.includes(p)) }));
  if (other.length) groups.push({ title: 'Other', permissions: other });
  return groups.filter((g) => g.permissions.length > 0);
}

/** Why a role cannot be deleted, as short phrases; empty when nothing uses it. */
export function usageReasons(u: RoleUsage): string[] {
  const out: string[] = [];
  if (u.members) out.push(`${u.members} ${u.members === 1 ? 'member' : 'members'}`);
  if (u.apiKeys) out.push(`${u.apiKeys} API ${u.apiKeys === 1 ? 'key' : 'keys'}`);
  if (u.workflows.length) out.push(`workflows ${u.workflows.map((w) => `“${w.name}”`).join(', ')}`);
  if (u.pendingApprovals) out.push(`${u.pendingApprovals} pending ${u.pendingApprovals === 1 ? 'approval' : 'approvals'}`);
  if (u.fields.length) out.push(`fields ${u.fields.map((f) => `“${f.label}”`).join(', ')}`);
  return out;
}

/** Default approver for a new approval step: Media Manager when it still exists, otherwise Administrator. */
export function defaultApprovalRole(keys: readonly string[]): string {
  return keys.includes('media_manager') ? 'media_manager' : 'admin';
}
